import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Save,
  Globe,
  GlobeLock,
  Loader2,
  Plus,
  GripVertical,
  Trash2,
  X,
  MapPin,
  AlertTriangle,
} from 'lucide-react'
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet'
import L from 'leaflet'
import { DndProvider, useDrag, useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import {
  getAdminWaypoints,
  createWaypoint,
  updateWaypoint,
  deleteWaypoint,
  reorderWaypoints,
  createHunt,
  updateHunt,
  publishHunt,
  unpublishHunt,
  setCoverImage,
  getHuntRewards,
  createReward,
  deleteReward,
  listSponsors,
} from '../api'
import type {
  AdminWaypoint,
  AdminReward,
  CreateWaypointPayload,
  WaypointHint,
  RewardType,
  SponsorOption,
} from '../api'
import { useAuthStore } from '../../stores/authStore'
import type {
  HuntDifficulty,
  HuntType,
  HuntDetail,
  ClueType,
  ChallengeType,
} from '../../types'
import { getValidAccessToken } from '../../lib/auth'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1'

// Numbered marker icon factory
function createNumberedIcon(num: number, isSelected: boolean) {
  const color = isSelected ? '#f59e0b' : '#78350f'
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42">
      <path d="M16 0C7.2 0 0 7.2 0 16c0 12 16 26 16 26s16-14 16-26C32 7.2 24.8 0 16 0z" fill="${color}"/>
      <circle cx="16" cy="16" r="10" fill="white"/>
      <text x="16" y="20" text-anchor="middle" font-size="12" font-weight="bold" fill="${color}">${num}</text>
    </svg>
  `
  return L.divIcon({
    html: svg,
    className: '',
    iconSize: [32, 42],
    iconAnchor: [16, 42],
  })
}

// Map click handler component
function MapClickHandler({
  onMapClick,
}: {
  onMapClick: (lat: number, lng: number) => void
}) {
  useMapEvents({
    click(e) {
      onMapClick(e.latlng.lat, e.latlng.lng)
    },
  })
  return null
}

// Map center updater
function MapCenterUpdater({ center }: { center: [number, number] | null }) {
  const map = useMap()
  const hasCentered = useRef(false)
  useEffect(() => {
    if (center && !hasCentered.current) {
      map.setView(center, 13)
      hasCentered.current = true
    }
  }, [center, map])
  return null
}

// Draggable waypoint row
const ITEM_TYPE = 'WAYPOINT_ROW'

interface DraggableRowProps {
  waypoint: AdminWaypoint
  index: number
  isSelected: boolean
  onSelect: () => void
  onDelete: () => void
  moveRow: (dragIndex: number, hoverIndex: number) => void
}

function DraggableWaypointRow({
  waypoint,
  index,
  isSelected,
  onSelect,
  onDelete,
  moveRow,
}: DraggableRowProps) {
  const ref = useRef<HTMLDivElement>(null)

  const [{ isDragging }, drag, preview] = useDrag({
    type: ITEM_TYPE,
    item: { index },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  })

  const [, drop] = useDrop({
    accept: ITEM_TYPE,
    hover(item: { index: number }, monitor) {
      if (!ref.current) return
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return

      const hoverBoundingRect = ref.current.getBoundingClientRect()
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      if (!clientOffset) return
      const hoverClientY = clientOffset.y - hoverBoundingRect.top

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return

      moveRow(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
  })

  preview(drop(ref))

  return (
    <div
      ref={ref}
      onClick={onSelect}
      className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 transition-colors ${
        isSelected
          ? 'border-[#f59e0b] bg-amber-50'
          : 'border-stone-200 bg-white hover:border-stone-300'
      } ${isDragging ? 'opacity-40' : ''}`}
    >
      <div ref={(node) => { drag(node) }} className="cursor-grab text-stone-400">
        <GripVertical className="h-4 w-4" />
      </div>
      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#78350f] text-xs font-bold text-white">
        {index + 1}
      </div>
      <span className="flex-1 truncate text-sm text-stone-700">
        {waypoint.place_name || `Waypoint ${index + 1}`}
      </span>
      <button
        onClick={(e) => {
          e.stopPropagation()
          onDelete()
        }}
        className="rounded p-1 text-stone-400 transition-colors hover:bg-red-50 hover:text-red-600"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

// Waypoint detail panel
interface WaypointDetailProps {
  waypoint: AdminWaypoint
  onUpdate: (updates: Partial<AdminWaypoint>) => void
  onClose: () => void
}

function WaypointDetailPanel({
  waypoint,
  onUpdate,
  onClose,
}: WaypointDetailProps) {
  const [placeName, setPlaceName] = useState(waypoint.place_name || '')
  const [clueType, setClueType] = useState<ClueType>(waypoint.clue_type || 'text')
  const [clueText, setClueText] = useState(waypoint.clue_text || '')
  const [hints, setHints] = useState<WaypointHint[]>(waypoint.hints || [])
  const [radius, setRadius] = useState(waypoint.arrival_radius_meters || 50)
  const [challengeType, setChallengeType] = useState<ChallengeType | 'none'>(
    waypoint.challenge_type || 'none'
  )
  // Challenge data fields
  const [questionText, setQuestionText] = useState('')
  const [answerText, setAnswerText] = useState('')
  const [qrValue, setQrValue] = useState('')
  const [qrHint, setQrHint] = useState('')
  const [obsQuestion, setObsQuestion] = useState('')
  const [obsOptions, setObsOptions] = useState<string[]>(['', '', '', ''])
  const [obsCorrectIndex, setObsCorrectIndex] = useState(0)

  // Populate challenge data from waypoint
  useEffect(() => {
    const data = waypoint.challenge_data || {}
    if (waypoint.challenge_type === 'question') {
      setQuestionText((data.question as string) || '')
      setAnswerText((data.answer as string) || '')
    } else if (waypoint.challenge_type === 'qr_scan') {
      setQrValue((data.expected_qr_value as string) || '')
      setQrHint((data.qr_hint as string) || '')
    } else if (waypoint.challenge_type === 'observation') {
      setObsQuestion((data.question as string) || '')
      setObsOptions((data.options as string[]) || ['', '', '', ''])
      setObsCorrectIndex((data.correct_option_index as number) || 0)
    }
  }, [waypoint.challenge_data, waypoint.challenge_type])

  const buildChallengeData = useCallback(() => {
    if (challengeType === 'none') return undefined
    if (challengeType === 'question') {
      return { question: questionText, answer: answerText }
    }
    if (challengeType === 'qr_scan') {
      return { expected_qr_value: qrValue, qr_hint: qrHint }
    }
    if (challengeType === 'observation') {
      return {
        question: obsQuestion,
        options: obsOptions,
        correct_option_index: obsCorrectIndex,
      }
    }
    return undefined
  }, [
    challengeType,
    questionText,
    answerText,
    qrValue,
    qrHint,
    obsQuestion,
    obsOptions,
    obsCorrectIndex,
  ])

  const handleSave = () => {
    onUpdate({
      place_name: placeName || undefined,
      clue_type: clueType,
      clue_text: clueText,
      hints: hints.filter((h) => h.text.trim().length > 0),
      arrival_radius_meters: radius,
      challenge_type: challengeType === 'none' ? undefined : (challengeType as ChallengeType),
      challenge_data: buildChallengeData(),
    })
  }

  const updateHint = (index: number, patch: Partial<WaypointHint>) => {
    setHints((prev) => prev.map((h, i) => (i === index ? { ...h, ...patch } : h)))
  }

  const addHint = () => {
    const lastDelay = hints.length > 0 ? hints[hints.length - 1].reveal_after_minutes : 0
    setHints((prev) => [...prev, { text: '', reveal_after_minutes: lastDelay + 3 }])
  }

  const removeHint = (index: number) => {
    setHints((prev) => prev.filter((_, i) => i !== index))
  }

  const inputClass =
    'w-full rounded-lg border border-stone-200 px-3 py-2 text-sm text-stone-900 placeholder:text-stone-400 focus:border-[#f59e0b] focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/20'
  const labelClass = 'mb-1 block text-xs font-medium text-stone-500'

  return (
    <div className="absolute right-0 top-0 z-[1000] flex h-full w-80 flex-col border-l border-stone-200 bg-white shadow-lg">
      <div className="flex items-center justify-between border-b border-stone-200 px-4 py-3">
        <h3 className="text-sm font-semibold text-stone-900">
          Waypoint Details
        </h3>
        <button
          onClick={onClose}
          className="rounded p-1 text-stone-400 hover:bg-stone-100 hover:text-stone-600"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 space-y-4 overflow-auto p-4">
        {/* Place name */}
        <div>
          <label className={labelClass}>Place Name</label>
          <input
            type="text"
            value={placeName}
            onChange={(e) => setPlaceName(e.target.value)}
            placeholder="e.g. Central Park Fountain"
            className={inputClass}
          />
        </div>

        {/* Clue type */}
        <div>
          <label className={labelClass}>Clue Type</label>
          <select
            value={clueType}
            onChange={(e) => setClueType(e.target.value as ClueType)}
            className={inputClass}
          >
            <option value="text">Text</option>
            <option value="riddle">Riddle</option>
            <option value="coordinates">Coordinates</option>
            <option value="image">Image</option>
          </select>
        </div>

        {/* Clue text */}
        <div>
          <label className={labelClass}>Clue Text</label>
          <textarea
            value={clueText}
            onChange={(e) => setClueText(e.target.value)}
            placeholder="Write a clue to guide hunters..."
            rows={3}
            className={inputClass}
          />
        </div>

        {/* Hints */}
        <div>
          <div className="mb-1 flex items-center justify-between">
            <label className={labelClass}>Progressive Hints</label>
            <button
              type="button"
              onClick={addHint}
              className="flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium text-[#f59e0b] hover:bg-amber-50"
            >
              <Plus className="h-3 w-3" />
              Add hint
            </button>
          </div>
          {hints.length === 0 && (
            <p className="text-xs text-stone-400">
              No hints. Hunters will only see the clue.
            </p>
          )}
          <div className="space-y-2">
            {hints.map((hint, index) => (
              <div
                key={index}
                className="rounded-lg border border-stone-200 bg-stone-50 p-2 space-y-2"
              >
                <div className="flex items-start gap-2">
                  <textarea
                    value={hint.text}
                    onChange={(e) => updateHint(index, { text: e.target.value })}
                    placeholder={`Hint ${index + 1}`}
                    rows={2}
                    className={inputClass}
                  />
                  <button
                    type="button"
                    onClick={() => removeHint(index)}
                    className="rounded p-1 text-stone-400 hover:bg-stone-200 hover:text-red-600"
                    aria-label="Remove hint"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                <label className="flex items-center gap-2 text-xs text-stone-500">
                  Reveal after
                  <input
                    type="number"
                    min={0}
                    max={120}
                    value={hint.reveal_after_minutes}
                    onChange={(e) =>
                      updateHint(index, {
                        reveal_after_minutes: Math.max(0, Number(e.target.value) || 0),
                      })
                    }
                    className="w-16 rounded border border-stone-200 px-2 py-0.5 text-xs focus:border-[#f59e0b] focus:outline-none"
                  />
                  minutes
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Arrival radius */}
        <div>
          <label className={labelClass}>
            Arrival Radius: {radius}m
          </label>
          <input
            type="range"
            min={10}
            max={100}
            step={5}
            value={radius}
            onChange={(e) => setRadius(Number(e.target.value))}
            className="w-full accent-[#f59e0b]"
          />
          <div className="flex justify-between text-xs text-stone-400">
            <span>10m</span>
            <span>100m</span>
          </div>
        </div>

        {/* Challenge type */}
        <div>
          <label className={labelClass}>Challenge Type</label>
          <select
            value={challengeType}
            onChange={(e) =>
              setChallengeType(e.target.value as ChallengeType | 'none')
            }
            className={inputClass}
          >
            <option value="none">No Challenge</option>
            <option value="question">Question</option>
            <option value="qr_scan">QR Scan</option>
            <option value="observation">Observation</option>
          </select>
        </div>

        {/* Challenge-specific fields */}
        {challengeType === 'question' && (
          <div className="space-y-3 rounded-lg border border-stone-200 bg-stone-50 p-3">
            <div>
              <label className={labelClass}>Question</label>
              <input
                type="text"
                value={questionText}
                onChange={(e) => setQuestionText(e.target.value)}
                placeholder="What year was this built?"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Answer</label>
              <input
                type="text"
                value={answerText}
                onChange={(e) => setAnswerText(e.target.value)}
                placeholder="1923"
                className={inputClass}
              />
            </div>
          </div>
        )}

        {challengeType === 'qr_scan' && (
          <div className="space-y-3 rounded-lg border border-stone-200 bg-stone-50 p-3">
            <div>
              <label className={labelClass}>Expected QR Value</label>
              <input
                type="text"
                value={qrValue}
                onChange={(e) => setQrValue(e.target.value)}
                placeholder="loot-seeker-wp-123"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>QR Hint</label>
              <input
                type="text"
                value={qrHint}
                onChange={(e) => setQrHint(e.target.value)}
                placeholder="Look under the bench..."
                className={inputClass}
              />
            </div>
          </div>
        )}

        {challengeType === 'observation' && (
          <div className="space-y-3 rounded-lg border border-stone-200 bg-stone-50 p-3">
            <div>
              <label className={labelClass}>Question</label>
              <input
                type="text"
                value={obsQuestion}
                onChange={(e) => setObsQuestion(e.target.value)}
                placeholder="What color is the door?"
                className={inputClass}
              />
            </div>
            {obsOptions.map((opt, i) => (
              <div key={i}>
                <label className={labelClass}>
                  Option {i + 1}
                  {i === obsCorrectIndex && (
                    <span className="ml-1 text-green-600">(correct)</span>
                  )}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={opt}
                    onChange={(e) => {
                      const updated = [...obsOptions]
                      updated[i] = e.target.value
                      setObsOptions(updated)
                    }}
                    placeholder={`Option ${i + 1}`}
                    className={inputClass}
                  />
                  <button
                    onClick={() => setObsCorrectIndex(i)}
                    className={`shrink-0 rounded-lg border px-2 text-xs transition-colors ${
                      i === obsCorrectIndex
                        ? 'border-green-300 bg-green-100 text-green-700'
                        : 'border-stone-200 text-stone-400 hover:border-green-300'
                    }`}
                  >
                    Correct
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Save button */}
      <div className="border-t border-stone-200 p-4">
        <button
          onClick={handleSave}
          className="w-full rounded-lg bg-gradient-to-r from-[#78350f] to-[#92400e] px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-opacity hover:opacity-90"
        >
          Save Waypoint
        </button>
      </div>
    </div>
  )
}

// Main HuntEditor component
export default function HuntEditor() {
  const { huntId } = useParams<{ huntId: string }>()
  const navigate = useNavigate()
  const isNew = huntId === 'new'

  // Hunt form state
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [difficulty, setDifficulty] = useState<HuntDifficulty>('medium')
  const [huntType, setHuntType] = useState<HuntType>('linear')
  const [duration, setDuration] = useState<number | undefined>(undefined)
  const [calculatedDistanceM, setCalculatedDistanceM] = useState<number | undefined>(undefined)
  const [coverImageUrl, setCoverImageUrl] = useState<string>('')
  const [status, setStatus] = useState<string>('draft')
  const [sponsorId, setSponsorId] = useState<string>('')
  const [sponsors, setSponsors] = useState<SponsorOption[]>([])
  const currentUser = useAuthStore((s) => s.user)
  const isAdmin = currentUser?.role === 'admin'

  // Waypoints
  const [waypoints, setWaypoints] = useState<AdminWaypoint[]>([])
  const [selectedWaypointId, setSelectedWaypointId] = useState<string | null>(null)

  // Rewards
  const [rewards, setRewards] = useState<AdminReward[]>([])
  const [showAddReward, setShowAddReward] = useState(false)
  const [newRewardName, setNewRewardName] = useState('')
  const [newRewardType, setNewRewardType] = useState<RewardType>('promo_code')
  const [newRewardCode, setNewRewardCode] = useState('')
  const [newRewardDescription, setNewRewardDescription] = useState('')
  const [newRewardQty, setNewRewardQty] = useState('')
  const [rewardSaving, setRewardSaving] = useState(false)

  // UI state
  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mapCenter, setMapCenter] = useState<[number, number] | null>(null)
  const [huntIdState, setHuntIdState] = useState<string | null>(
    isNew ? null : huntId || null
  )

  const selectedWaypoint = waypoints.find((w) => w.id === selectedWaypointId) || null

  // Load sponsors list (admin only)
  useEffect(() => {
    if (!isAdmin) return
    listSponsors()
      .then(setSponsors)
      .catch((err) => console.error('Failed to load sponsors:', err))
  }, [isAdmin])

  // Get user's location for map center
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setMapCenter([pos.coords.latitude, pos.coords.longitude]),
        () => setMapCenter([0, 0])
      )
    } else {
      setMapCenter([0, 0])
    }
  }, [])

  // Load hunt data if editing
  useEffect(() => {
    if (isNew) return

    async function loadHunt() {
      try {
        setLoading(true)
        const token = await getValidAccessToken()
        if (!token) throw new Error('Not authenticated')

        const res = await fetch(`${API_URL}/hunts/${huntId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        })
        if (!res.ok) throw new Error('Failed to load hunt')
        const hunt: HuntDetail = await res.json()

        setTitle(hunt.title)
        setDescription(hunt.description)
        setDifficulty(hunt.difficulty)
        setHuntType(hunt.hunt_type)
        setDuration(hunt.estimated_duration_minutes)
        setCalculatedDistanceM(hunt.total_distance_meters ?? undefined)
        setCoverImageUrl(hunt.cover_image_url || '')
        setStatus(hunt.status)
        setSponsorId(hunt.sponsor?.id || '')

        // Load waypoints and rewards in parallel
        const [wp, rw] = await Promise.all([
          getAdminWaypoints(huntId!),
          getHuntRewards(huntId!),
        ])
        setWaypoints(wp)
        setRewards(rw)

        // Center map on first waypoint if available
        if (wp.length > 0) {
          setMapCenter([wp[0].latitude, wp[0].longitude])
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load hunt')
      } finally {
        setLoading(false)
      }
    }

    loadHunt()
  }, [huntId, isNew])

  // Save hunt (create or update)
  const handleSave = async () => {
    if (!title.trim()) {
      setError('Title is required')
      return
    }

    try {
      setSaving(true)
      setError(null)

      if (isNew && !huntIdState) {
        const hunt = await createHunt({
          title: title.trim(),
          description: description.trim(),
          difficulty,
          hunt_type: huntType,
          estimated_duration_minutes: duration,
          ...(isAdmin ? { sponsor_id: sponsorId || null } : {}),
        })
        if (coverImageUrl.trim()) {
          await setCoverImage(hunt.id, coverImageUrl.trim())
        }
        setHuntIdState(hunt.id)
        navigate(`/admin/hunts/${hunt.id}`, { replace: true })
      } else {
        const id = huntIdState || huntId
        if (!id) return
        await updateHunt(id, {
          title: title.trim(),
          description: description.trim(),
          difficulty,
          hunt_type: huntType,
          estimated_duration_minutes: duration,
          ...(isAdmin ? { sponsor_id: sponsorId || null } : {}),
        })
        await setCoverImage(id, coverImageUrl.trim() || null)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save hunt')
    } finally {
      setSaving(false)
    }
  }

  // Toggle publish
  const handleTogglePublish = async () => {
    const id = huntIdState || huntId
    if (!id) return
    if (status !== 'published') {
      if (waypoints.length < 2) {
        window.alert(
          `Hunts need at least 2 waypoints before publishing. This hunt has ${waypoints.length}.`
        )
        return
      }
      const missing: string[] = []
      if (!duration) missing.push('estimated duration')
      if (!calculatedDistanceM || calculatedDistanceM === 0)
        missing.push('waypoints (distance is 0)')
      if (missing.length > 0) {
        const ok = window.confirm(
          `This hunt is missing: ${missing.join(' and ')}.\n\nPublish anyway?`
        )
        if (!ok) return
      }
    }
    try {
      setSaving(true)
      if (status === 'published') {
        await unpublishHunt(id)
        setStatus('draft')
      } else {
        await publishHunt(id)
        setStatus('published')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status')
    } finally {
      setSaving(false)
    }
  }

  // Add waypoint via map click
  const handleMapClick = async (lat: number, lng: number) => {
    const id = huntIdState || huntId
    if (!id) {
      setError('Save the hunt first before adding waypoints')
      return
    }

    try {
      const payload: CreateWaypointPayload = {
        latitude: lat,
        longitude: lng,
        arrival_radius_meters: 50,
        clue_text: '',
        clue_type: 'text',
      }
      const wp = await createWaypoint(id, payload)
      setWaypoints((prev) => [...prev, wp])
      setSelectedWaypointId(wp.id)
      await refreshDistance()
    } catch (err) {
      console.error('Failed to create waypoint:', err)
    }
  }

  // Update waypoint
  const handleUpdateWaypoint = async (updates: Partial<AdminWaypoint>) => {
    if (!selectedWaypointId) return
    try {
      const updated = await updateWaypoint(selectedWaypointId, updates)
      setWaypoints((prev) =>
        prev.map((w) => (w.id === selectedWaypointId ? updated : w))
      )
      if (updates.latitude !== undefined || updates.longitude !== undefined) {
        await refreshDistance()
      }
    } catch (err) {
      console.error('Failed to update waypoint:', err)
    }
  }

  // Delete waypoint
  const handleDeleteWaypoint = async (waypointId: string) => {
    try {
      await deleteWaypoint(waypointId)
      setWaypoints((prev) => prev.filter((w) => w.id !== waypointId))
      if (selectedWaypointId === waypointId) setSelectedWaypointId(null)
      await refreshDistance()
    } catch (err) {
      console.error('Failed to delete waypoint:', err)
    }
  }

  // Reorder waypoints via drag
  const moveRow = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      setWaypoints((prev) => {
        const updated = [...prev]
        const [removed] = updated.splice(dragIndex, 1)
        updated.splice(hoverIndex, 0, removed)
        return updated
      })
    },
    []
  )

  // Refresh calculated distance from server
  const refreshDistance = useCallback(async () => {
    const id = huntIdState || huntId
    if (!id) return
    try {
      const token = await getValidAccessToken()
      const res = await fetch(`${API_URL}/hunts/${id}`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      })
      if (res.ok) {
        const hunt = await res.json()
        setCalculatedDistanceM(hunt.total_distance_meters ?? undefined)
      }
    } catch {
      // non-critical
    }
  }, [huntIdState, huntId])

  // Persist reorder on drop
  const handleReorderDone = async () => {
    const id = huntIdState || huntId
    if (!id) return
    try {
      await reorderWaypoints(id, waypoints.map((w) => w.id))
      await refreshDistance()
    } catch (err) {
      console.error('Failed to reorder waypoints:', err)
    }
  }

  // Reward handlers
  const handleAddReward = async () => {
    const id = huntIdState || huntId
    if (!id || !newRewardName.trim()) return
    setRewardSaving(true)
    try {
      const r = await createReward(id, {
        name: newRewardName.trim(),
        type: newRewardType,
        description: newRewardDescription || undefined,
        code_value: newRewardCode || undefined,
        total_quantity: newRewardQty ? parseInt(newRewardQty) : undefined,
      })
      setRewards((prev) => [...prev, r])
      setShowAddReward(false)
      setNewRewardName('')
      setNewRewardCode('')
      setNewRewardDescription('')
      setNewRewardQty('')
    } catch (err) {
      console.error('Failed to create reward:', err)
    } finally {
      setRewardSaving(false)
    }
  }

  const handleDeleteReward = async (rewardId: string) => {
    try {
      await deleteReward(rewardId)
      setRewards((prev) => prev.filter((r) => r.id !== rewardId))
    } catch (err) {
      console.error('Failed to delete reward:', err)
    }
  }

  const inputClass =
    'w-full rounded-lg border border-stone-200 px-3 py-2 text-sm text-stone-900 placeholder:text-stone-400 focus:border-[#f59e0b] focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/20'
  const labelClass = 'mb-1 block text-xs font-medium text-stone-500'

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#f59e0b]" />
      </div>
    )
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="flex h-full flex-col">
        {/* Top bar */}
        <div className="flex items-center justify-between border-b border-stone-200 bg-white px-6 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin/hunts')}
              className="rounded-lg p-2 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-600"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-stone-900">
                {title || 'New Hunt'}
              </h1>
              <p className="text-xs text-stone-500">
                {waypoints.length} waypoint{waypoints.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {error && (
              <span className="text-sm text-red-600">{error}</span>
            )}
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#78350f] to-[#92400e] px-4 py-2 text-sm font-medium text-white shadow-sm transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save Changes
            </button>
            {(huntIdState || !isNew) && (
              <button
                onClick={handleTogglePublish}
                disabled={saving}
                className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50 ${
                  status === 'published'
                    ? 'border-stone-200 text-stone-700 hover:bg-stone-50'
                    : 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100'
                }`}
              >
                {status === 'published' ? (
                  <>
                    <GlobeLock className="h-4 w-4" />
                    Unpublish
                  </>
                ) : (
                  <>
                    <Globe className="h-4 w-4" />
                    Publish
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Content area: left panel + right map */}
        <div className="relative flex flex-1 overflow-hidden">
          {/* Left panel - form + waypoint list */}
          <div className="flex w-[40%] flex-col overflow-auto border-r border-stone-200 bg-white">
            <div className="space-y-4 p-6">
              {/* Warning: too few waypoints to publish */}
              {waypoints.length < 2 && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <div>
                    <strong>Add at least 2 waypoints before publishing</strong>{' '}
                    — distance can't be calculated with fewer.
                    {waypoints.length === 0
                      ? ' This hunt has none yet.'
                      : ' This hunt has only 1.'}
                  </div>
                </div>
              )}

              {/* Warning: published hunt missing duration/distance */}
              {status === 'published' &&
                (!duration ||
                  !calculatedDistanceM ||
                  calculatedDistanceM === 0) && (
                  <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <div>
                      <strong>Missing info on a published hunt:</strong>{' '}
                      {!duration && !calculatedDistanceM
                        ? 'no estimated duration and no waypoints (so no distance).'
                        : !duration
                          ? 'no estimated duration set.'
                          : 'no waypoints — distance is 0.'}
                    </div>
                  </div>
                )}

              {/* Title */}
              <div>
                <label className={labelClass}>Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter hunt title..."
                  className={inputClass}
                />
              </div>

              {/* Description */}
              <div>
                <label className={labelClass}>Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe this treasure hunt..."
                  rows={3}
                  className={inputClass}
                />
              </div>

              {/* Row: Difficulty + Hunt Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Difficulty</label>
                  <select
                    value={difficulty}
                    onChange={(e) =>
                      setDifficulty(e.target.value as HuntDifficulty)
                    }
                    className={inputClass}
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Hunt Type</label>
                  <select
                    value={huntType}
                    onChange={(e) => setHuntType(e.target.value as HuntType)}
                    className={inputClass}
                  >
                    <option value="linear">Linear</option>
                    <option value="hub_spoke">Hub & Spoke</option>
                    <option value="branching">Branching</option>
                    <option value="open">Open</option>
                  </select>
                </div>
              </div>

              {/* Distance (auto-calculated) */}
              <div>
                <label className={labelClass}>Distance (auto-calculated)</label>
                <div className="flex items-center gap-2 rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-sm text-stone-700">
                  {calculatedDistanceM !== undefined && calculatedDistanceM > 0
                    ? `${(calculatedDistanceM / 1000).toFixed(2)} km`
                    : <span className="text-stone-400">Add waypoints to calculate</span>}
                </div>
              </div>

              {/* Duration */}
              <div>
                <label className={labelClass}>
                  Estimated Duration (minutes)
                  {calculatedDistanceM && calculatedDistanceM > 0 && (
                    <span className="ml-2 font-normal text-stone-400">
                      (~{Math.round(calculatedDistanceM / 1000 / 5 * 60)} min walk at 5 km/h)
                    </span>
                  )}
                </label>
                <input
                  type="number"
                  value={duration ?? ''}
                  onChange={(e) =>
                    setDuration(
                      e.target.value ? Number(e.target.value) : undefined
                    )
                  }
                  placeholder="60"
                  min={1}
                  className={inputClass}
                />
              </div>
            </div>

            {/* Sponsor selector (admin only) */}
            {isAdmin && (
              <div className="mt-4">
                <label className={labelClass}>Sponsor</label>
                <select
                  value={sponsorId}
                  onChange={(e) => setSponsorId(e.target.value)}
                  className={inputClass}
                >
                  <option value="">— No sponsor —</option>
                  {sponsors.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.business_name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-stone-500">
                  Only profiles with role <code>business</code> appear here.
                </p>
              </div>
            )}

            {/* Cover image */}
            <div className="mt-4">
              <label className={labelClass}>Cover Image URL</label>
              <input
                type="url"
                value={coverImageUrl}
                onChange={(e) => setCoverImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
                className={inputClass}
              />
              {coverImageUrl && (
                <img
                  src={coverImageUrl}
                  alt="Cover preview"
                  className="mt-2 h-32 w-full rounded-lg object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none'
                  }}
                />
              )}
            </div>

            {/* Waypoint list */}
            <div className="border-t border-stone-200 p-6">
              <h3 className="mb-3 text-sm font-semibold text-stone-900">
                Waypoints
              </h3>
              {waypoints.length === 0 ? (
                <div className="flex flex-col items-center rounded-lg border border-dashed border-stone-300 py-8">
                  <MapPin className="mb-2 h-6 w-6 text-stone-400" />
                  <p className="text-sm text-stone-500">
                    Click the map to add waypoints
                  </p>
                </div>
              ) : (
                <div
                  className="space-y-2"
                  onMouseUp={handleReorderDone}
                >
                  {waypoints.map((wp, index) => (
                    <DraggableWaypointRow
                      key={wp.id}
                      waypoint={wp}
                      index={index}
                      isSelected={selectedWaypointId === wp.id}
                      onSelect={() => setSelectedWaypointId(wp.id)}
                      onDelete={() => handleDeleteWaypoint(wp.id)}
                      moveRow={moveRow}
                    />
                  ))}
                </div>
              )}
              {(huntIdState || !isNew) && (
                <button
                  onClick={() => {
                    if (!mapCenter) return
                    handleMapClick(mapCenter[0], mapCenter[1])
                  }}
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-stone-300 py-2 text-sm text-stone-500 transition-colors hover:border-[#f59e0b] hover:text-[#78350f]"
                >
                  <Plus className="h-4 w-4" />
                  Add Waypoint
                </button>
              )}
            </div>
          </div>

          {/* Rewards section — only shown for existing hunts */}
          {!isNew && (
            <div className="border-t border-stone-200 p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-stone-900">Rewards</h3>
                {!showAddReward && (
                  <button
                    onClick={() => setShowAddReward(true)}
                    className="flex items-center gap-1 text-xs text-[#78350f] hover:text-[#f59e0b] font-medium"
                  >
                    <Plus className="h-3.5 w-3.5" /> Add Reward
                  </button>
                )}
              </div>

              {rewards.length === 0 && !showAddReward && (
                <p className="text-xs text-stone-400 text-center py-4">
                  No rewards yet. Hunters will complete this hunt for glory alone.
                </p>
              )}

              {rewards.map((r) => {
                const isPool = r.total_quantity != null && r.total_quantity > 0
                const remaining = isPool ? Math.max(0, (r.total_quantity ?? 0) - r.claimed_quantity) : null
                const isExhausted = isPool && r.claimed_quantity >= (r.total_quantity ?? 0)
                return (
                  <div key={r.id} className="flex items-start justify-between gap-3 rounded-lg border border-stone-100 bg-stone-50 px-3 py-2.5 mb-2">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-stone-800">{r.name}</span>
                        <span className="text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">{r.type}</span>
                        {isPool && (
                          <span
                            className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                              isExhausted
                                ? 'bg-stone-200 text-stone-600'
                                : 'bg-emerald-100 text-emerald-800'
                            }`}
                          >
                            {isExhausted
                              ? `Pool reward — exhausted`
                              : `Pool reward — ${remaining} available`}
                          </span>
                        )}
                      </div>
                      {r.code_value && (
                        <p className="text-xs font-mono text-stone-600 mt-0.5 bg-white border border-stone-200 px-2 py-0.5 rounded inline-block">{r.code_value}</p>
                      )}
                      {r.description && <p className="text-xs text-stone-500 mt-0.5">{r.description}</p>}
                      {isPool ? (
                        <p className="text-xs text-stone-500 mt-0.5">
                          First {r.total_quantity} finishers &middot; {r.claimed_quantity}/{r.total_quantity} claimed
                        </p>
                      ) : null}
                    </div>
                    <button onClick={() => handleDeleteReward(r.id)} className="shrink-0 text-stone-300 hover:text-red-400">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )
              })}

              {showAddReward && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2.5">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className={labelClass}>Reward Name</label>
                      <input
                        type="text"
                        value={newRewardName}
                        onChange={(e) => setNewRewardName(e.target.value)}
                        placeholder="e.g. 20% off coffee"
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Type</label>
                      <select
                        value={newRewardType}
                        onChange={(e) => setNewRewardType(e.target.value as RewardType)}
                        className={inputClass}
                      >
                        <option value="promo_code">Promo Code</option>
                        <option value="message">Message</option>
                        <option value="points">Points</option>
                        <option value="physical">Physical</option>
                      </select>
                    </div>
                  </div>
                  {newRewardType === 'promo_code' && (
                    <div>
                      <label className={labelClass}>Promo Code</label>
                      <input
                        type="text"
                        value={newRewardCode}
                        onChange={(e) => setNewRewardCode(e.target.value.toUpperCase())}
                        placeholder="e.g. TREASURE20"
                        className={`${inputClass} font-mono`}
                      />
                    </div>
                  )}
                  <div>
                    <label className={labelClass}>Description (optional)</label>
                    <input
                      type="text"
                      value={newRewardDescription}
                      onChange={(e) => setNewRewardDescription(e.target.value)}
                      placeholder="Show this code at the register"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>First N finishers (leave blank for unlimited)</label>
                    <input
                      type="number"
                      value={newRewardQty}
                      onChange={(e) => setNewRewardQty(e.target.value)}
                      placeholder="Unlimited"
                      min={1}
                      className={inputClass}
                    />
                    <p className="mt-1 text-xs text-stone-500">
                      Set a number to limit this reward to the first N hunters who complete the hunt. Once the pool runs out, later finishers see no reward.
                    </p>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={() => setShowAddReward(false)}
                      className="flex-1 rounded-lg border border-stone-200 bg-white py-1.5 text-xs font-medium text-stone-600 hover:bg-stone-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddReward}
                      disabled={!newRewardName.trim() || rewardSaving}
                      className="flex-1 rounded-lg bg-[#78350f] py-1.5 text-xs font-medium text-white hover:bg-[#92400e] disabled:opacity-50"
                    >
                      {rewardSaving ? 'Saving…' : 'Add Reward'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Right panel - Map */}
          <div className="relative flex-1">
            {mapCenter && (
              <MapContainer
                center={mapCenter}
                zoom={13}
                className="h-full w-full"
                style={{ background: '#f0ede8' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                  url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                />
                <MapClickHandler onMapClick={handleMapClick} />
                <MapCenterUpdater center={mapCenter} />
                {waypoints.map((wp, index) => (
                  <Marker
                    key={wp.id}
                    position={[wp.latitude, wp.longitude]}
                    icon={createNumberedIcon(
                      index + 1,
                      selectedWaypointId === wp.id
                    )}
                    eventHandlers={{
                      click: () => setSelectedWaypointId(wp.id),
                    }}
                  />
                ))}
              </MapContainer>
            )}

            {/* Waypoint detail slide-in */}
            {selectedWaypoint && (
              <WaypointDetailPanel
                waypoint={selectedWaypoint}
                onUpdate={handleUpdateWaypoint}
                onClose={() => setSelectedWaypointId(null)}
              />
            )}
          </div>
        </div>
      </div>
    </DndProvider>
  )
}
